import React, { useState } from 'react';
import { Box, Typography, Button, Alert, CircularProgress, Stack, Divider } from '@mui/material';
import { Security, QrCode, Warning } from '@mui/icons-material';
import { motion } from 'framer-motion';
import { supabase } from '../../supabaseClient';
import { useTheme } from '../../ThemeContext';
import { enrollTotp } from '../../utils/mfa';
import { translateAuthError } from '../../utils/authErrors';
import AuthCardShell from './AuthCardShell';
import MandatoryTwoFactorSetupDialog from './MandatoryTwoFactorSetupDialog';

// Kop: waarschuwingsicoon, titel en uitleg
function SetupHeader({ theme }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 4 }}>
      <motion.div
        whileHover={{ scale: 1.05, rotate: 2 }}
        transition={{ type: 'spring', stiffness: 300 }}
      >
        <Box
          sx={{
            width: 80,
            height: 80,
            mb: 2,
            borderRadius: 3,
            background: `linear-gradient(135deg, ${theme.palette.warning.main} 0%, ${theme.palette.error.main} 100%)`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: `0 8px 20px ${theme.palette.warning.main}40`,
          }}
        >
          <Warning sx={{ fontSize: 40, color: 'white' }} />
        </Box>
      </motion.div>
      <Typography
        variant="h4"
        component="h1"
        sx={{
          fontWeight: 'bold',
          background: `linear-gradient(135deg, ${theme.palette.warning.main} 0%, ${theme.palette.error.main} 100%)`,
          backgroundClip: 'text',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          mb: 1,
          textAlign: 'center',
        }}
      >
        Beveiliging Vereist
      </Typography>
      <Typography
        variant="body1"
        sx={{
          color: 'text.secondary',
          textAlign: 'center',
          fontWeight: 500,
          lineHeight: 1.6,
        }}
      >
        Je moet twee-factor authenticatie instellen om door te gaan
      </Typography>
    </Box>
  );
}

// Uitleg waarom 2FA verplicht is en wat de gebruiker nodig heeft
function SetupIntro() {
  return (
    <>
      <Alert severity="warning" sx={{ borderRadius: 2 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
          Waarom is dit vereist?
        </Typography>
        <Typography variant="body2">
          Voor de veiligheid van je account en gegevens is twee-factor authenticatie verplicht. Dit
          duurt slechts een paar minuten om in te stellen.
        </Typography>
      </Alert>

      <Box sx={{ textAlign: 'center' }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          Wat heb je nodig?
        </Typography>
        <Stack
          direction="row"
          spacing={2}
          sx={{
            justifyContent: 'center',
            flexWrap: 'wrap',
          }}
        >
          <Box sx={{ textAlign: 'center', minWidth: 120 }}>
            <Security sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
            <Typography variant="body2">Authenticator App</Typography>
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
              }}
            >
              Google Authenticator, 1Password, etc.
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'center', minWidth: 120 }}>
            <QrCode sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
            <Typography variant="body2">QR Code Scannen</Typography>
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
              }}
            >
              Koppel je app aan je account
            </Typography>
          </Box>
        </Stack>
      </Box>

      <Divider />
    </>
  );
}

// Startknop, uitlogknop en voetnoot
function SetupActions({ theme, loading, onStart, onCancel }) {
  return (
    <Box sx={{ textAlign: 'center' }}>
      <Button
        variant="contained"
        size="large"
        startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <Security />}
        onClick={onStart}
        disabled={loading}
        sx={{
          py: 1.5,
          px: 4,
          borderRadius: 3,
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          boxShadow: `0 4px 12px ${theme.palette.primary.main}40`,
          fontSize: '1.1rem',
          fontWeight: 600,
          '&:hover': {
            background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
            boxShadow: `0 6px 20px ${theme.palette.primary.main}60`,
          },
        }}
      >
        {loading ? 'Bezig met instellen...' : '2FA Nu Instellen'}
      </Button>
      {onCancel && (
        <Button
          variant="text"
          size="small"
          onClick={onCancel}
          disabled={loading}
          sx={{ display: 'block', mx: 'auto', mt: 1 }}
        >
          Uitloggen / ander account
        </Button>
      )}
      <Typography
        variant="caption"
        sx={{
          display: 'block',
          mt: 2,
          color: 'text.secondary',
        }}
      >
        Dit is een eenmalige setup en duurt ongeveer 2 minuten
      </Typography>
    </Box>
  );
}

const MandatoryTwoFactorSetup = ({ user, onComplete, onCancel }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showSetupDialog, setShowSetupDialog] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [secret, setSecret] = useState('');
  const [factorId, setFactorId] = useState('');
  const [verifyCode, setVerifyCode] = useState('');
  const { theme } = useTheme();

  // Start verplichte 2FA setup
  const startMandatorySetup = async () => {
    try {
      setLoading(true);
      setError(null);

      // Ruimt eerst afgebroken pogingen op (anders faalt een tweede poging)
      const data = await enrollTotp(user.email);

      setFactorId(data.id);
      setSecret(data.totp.secret);

      // Genereer QR code voor de secret
      const otpauth = `otpauth://totp/BEM.report:${user.email}?secret=${data.totp.secret}&issuer=BEM.report`;
      // qrcode pas laden als er echt een QR-code nodig is
      const { default: QRCode } = await import('qrcode');
      const qrUrl = await QRCode.toDataURL(otpauth);
      setQrCodeUrl(qrUrl);

      setShowSetupDialog(true);
    } catch (error) {
      console.error('Error starting mandatory 2FA setup:', error);
      setError(
        translateAuthError(error, 'Het instellen van 2FA is niet gelukt. Probeer het opnieuw.'),
      );
    } finally {
      setLoading(false);
    }
  };

  // Verificeer 2FA code en voltooi verplichte setup
  const verifyAndComplete = async () => {
    try {
      setLoading(true);
      setError(null);

      if (!verifyCode || verifyCode.length !== 6) {
        throw new Error('Voer een geldige 6-cijferige code in');
      }

      // Maak een challenge voor verificatie
      const { data: challengeData, error: challengeError } = await supabase.auth.mfa.challenge({
        factorId: factorId,
      });

      if (challengeError) throw challengeError;

      // Verificeer de code
      const { error: verifyError } = await supabase.auth.mfa.verify({
        factorId: factorId,
        challengeId: challengeData.id,
        code: verifyCode,
      });

      if (verifyError) throw verifyError;

      // Setup voltooid! Ga naar normale app
      onComplete();
    } catch (error) {
      console.error('Error verifying mandatory 2FA code:', error);
      setError(translateAuthError(error, 'Verificatie mislukt. Probeer het opnieuw.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <AuthCardShell
        theme={theme}
        maxWidth="sm"
        accentGradient={`linear-gradient(90deg, ${theme.palette.warning.main} 0%, ${theme.palette.error.main} 100%)`}
      >
        <SetupHeader theme={theme} />

        {error && (
          <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Stack spacing={3}>
          <SetupIntro />
          <SetupActions
            theme={theme}
            loading={loading}
            onStart={startMandatorySetup}
            onCancel={onCancel}
          />
        </Stack>
      </AuthCardShell>

      {/* Setup Dialog */}
      <MandatoryTwoFactorSetupDialog
        open={showSetupDialog}
        qrCodeUrl={qrCodeUrl}
        secret={secret}
        verifyCode={verifyCode}
        setVerifyCode={setVerifyCode}
        error={error}
        loading={loading}
        onVerify={verifyAndComplete}
      />
    </>
  );
};

export default MandatoryTwoFactorSetup;
